import { DatePicker } from "antd";
import dayjs from "dayjs";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import CommonSelect from "../../../core/common/commonSelect";
import MultiSelectSection, { Option } from "../../../core/common/multiSelect";
import {
  feeGroup,
  feesTypes,
  frequencies,
} from "../../../core/common/selectoption/selectoption";
import {
  selectCurrentSession,
  selectSessions,
} from "../../../core/data/redux/slice/sessionsSlice";
import { Class, getAllClasses } from "../../../service/classes";
import {
  createFeeGroup,
  deleteFeeGroup,
  FEE_GROUP,
  FEE_GROUP_PAYLOAD,
  updateFeeGroup,
} from "../../../service/fee-group";
import {
  createFeeType,
  deleteFeeType,
  FeeRecord,
  updateFeeType,
} from "../../../service/fees";
import { MAP_STATUS } from "../../../constant";

interface FeeTypeData {
  name: string;
  classes: string[];
  amount?: number;
  feeGroup: string;
  status: boolean;
  system: string;
  session: string;
  frequency: string;
  dueAfterDays?: number;
  examinationFeeDate?: string;
}

interface FeesModalProps {
  refreshList: () => void;
  editingGroup?: FEE_GROUP_PAYLOAD;
  setEditingGroup?: (value?: FEE_GROUP_PAYLOAD) => void;
  selectedSystem: string;
  editFee?: FeeRecord | null;
  setEditFee?: (feeRecord: FeeRecord | null) => void;
  feeGroups?: FEE_GROUP[];
  classes?: Class[];
}

const FeesModal = ({
  refreshList,
  editingGroup,
  setEditingGroup = () => null,
  selectedSystem,
  editFee,
  setEditFee = () => null,
  feeGroups = [],
  classes = [],
}: FeesModalProps) => {
  const currentSession = useSelector(selectCurrentSession);
  const sessions = useSelector(selectSessions);
  const [activeContent, setActiveContent] = useState("");
  const [feeGroupData, setFeeGroupData] = useState<
    Omit<FEE_GROUP_PAYLOAD, "status"> & { status: boolean }
  >({
    name: "",
    description: "",
    session: currentSession?._id || "",
    system: selectedSystem,
    status: true,
  });

  const [feeTypeData, setFeeTypeData] = useState<FeeTypeData>({
    name: "",
    feeGroup: "",
    amount: 0,
    classes: [],
    frequency: "",
    status: true,
    system: selectedSystem,
    session: currentSession?._id || "",
    dueAfterDays: 0,
    examinationFeeDate: "",
  });

  const [feeTypeEditData, setFeeTypeEditData] = useState<FeeTypeData>({
    name: "",
    feeGroup: "",
    amount: 0,
    classes: [],
    frequency: "",
    status: true,
    system: selectedSystem,
    session: "",
    dueAfterDays: 0,
    examinationFeeDate: "",
  });

  const [selectedEditClasses, setSelectedEditClasses] = useState<Option[]>([]);
  const [filteredClassOptions, setFilteredClassOptions] = useState<Option[]>(
    []
  );
  const [selectedClasses, setSelectedClasses] = useState<Option[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const day = String(today.getDate()).padStart(2, "0");
  const formattedDate = `${month}-${day}-${year}`;
  const defaultValue = dayjs(formattedDate);

  const isExaminationFeeGroup = (feeGroupId: string) => {
    if (!feeGroupId) return false;
    const selectedGroup = feeGroups.find((group) => group._id === feeGroupId);
    if (!selectedGroup) return false;
    const groupName = selectedGroup.name?.toLowerCase().trim();
    return (
      groupName === "examination" ||
      groupName === "exam" ||
      groupName === "exam fees" ||
      groupName.includes("exam")
    );
  };

  const getModalContainer = () => {
    const modalElement = document.getElementById("modal-datepicker");
    return modalElement ? modalElement : document.body;
  };

  const getModalContainer2 = () => {
    const modalElement = document.getElementById("modal-datepicker2");
    return modalElement ? modalElement : document.body;
  };

  useEffect(() => {
    if (editingGroup) {
      setFeeGroupData({
        ...editingGroup,
        status: editingGroup.status === MAP_STATUS.active,
      });
    } else {
      setFeeGroupData({
        name: "",
        description: "",
        status: true,
        session: currentSession?._id || "",
        system: selectedSystem,
      });
    }
  }, [editingGroup, currentSession, selectedSystem]);

  useEffect(() => {
    if (editFee?._id) {
      setFeeTypeEditData({
        ...editFee,
        feeGroup: editFee.feeGroup._id,
        classes: editFee.classes.map((item) => item._id),
        status: editFee.status === MAP_STATUS.active,
        examinationFeeDate: editFee.applicableFrom || "",
      });
      setSelectedEditClasses(
        editFee.classes.map((item) => ({ label: item.name, value: item._id }))
      );
    }
  }, [editFee?._id]);

  const handleContentChange = (event: any) => {
    setActiveContent(event.target.value);
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFeeGroupData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFeeTypeInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFeeTypeData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFeeTypeEditInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFeeTypeEditData((prev) => ({ ...prev, [name]: value }));
  };

  const handleStatusChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFeeGroupData((prev) => ({ ...prev, status: e.target.checked }));
  };

  const handleFeeTypeStatusChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFeeTypeData((prev) => ({ ...prev, status: e.target.checked }));
  };

  const handleCreateSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await createFeeGroup({
        ...feeGroupData,
        status: feeGroupData.status ? "active" : "inactive",
        session: currentSession?._id || "",
        system: feeGroupData.system as string,
      });
      toast.success("Fee group created successfully!");
      const el = document.getElementById("add_fees_group");
      if (el) {
        el.setAttribute("data-bs-dismiss", "modal");
        el.click();
        el.removeAttribute("data-bs-dismiss");
      }
      refreshList();
      resetForm();
    } catch (error) {
      toast.error("Failed to create fee group");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCreateFeeTypeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!feeTypeData.feeGroup) {
      toast.error("Please select a fee group");
      return;
    }

    setIsSubmitting(true);

    try {
      const payload: any = {
        name: feeTypeData.name,
        feeGroup: feeTypeData.feeGroup,
        amount: Number(feeTypeData.amount) || 0,
        classes: feeTypeData.classes || [],
        status: feeTypeData.status ? "active" : "inactive",
        session: feeTypeData.session,
        frequency: feeTypeData.frequency,
        dueAfterDays: feeTypeData.dueAfterDays as number,
        system: selectedSystem,
      };

      if (isExaminationFeeGroup(feeTypeData.feeGroup)) {
        if (!feeTypeData.examinationFeeDate) {
          toast.error(
            "Examination Fee Generation Date is required for examination fees"
          );
          setIsSubmitting(false);
          return;
        }
        payload.applicableFrom = feeTypeData.examinationFeeDate;
      }

      await createFeeType(payload);
      toast.success("Fee type created successfully!");
      const el = document.getElementById("add_fees_Type");
      if (el) {
        el.setAttribute("data-bs-dismiss", "modal");
        el.click();
        el.removeAttribute("data-bs-dismiss");
      }
      refreshList();
      resetFeeTypeForm();
    } catch (error) {
      console.error("Error creating fee type:", error);
      toast.error("Failed to create fee type");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingGroup) return;
    setIsSubmitting(true);

    try {
      await updateFeeGroup(editingGroup?._id as string, {
        ...feeGroupData,
        status: feeGroupData.status ? "active" : "inactive",
        session: currentSession?._id || "",
        system: feeGroupData.system,
      });
      toast.success("Fee group updated successfully!");
      const el = document.getElementById("edit_fees_group");
      if (el) {
        el.setAttribute("data-bs-dismiss", "modal");
        el.click();
        el.removeAttribute("data-bs-dismiss");
      }
      refreshList();
    } catch (error) {
      toast.error("Failed to update fee group");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateFeeTypeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editFee) return;
    setIsSubmitting(true);

    try {
      const payload: any = {
        name: feeTypeEditData.name,
        feeGroup: feeTypeEditData.feeGroup,
        amount: feeTypeEditData.amount,
        classes: feeTypeEditData.classes,
        status: feeTypeEditData.status ? "active" : "inactive",
        system: selectedSystem,
        session: feeTypeEditData.session,
        frequency: feeTypeEditData.frequency,
        dueAfterDays: feeTypeEditData.dueAfterDays,
      };

      if (isExaminationFeeGroup(feeTypeEditData.feeGroup)) {
        if (!feeTypeEditData.examinationFeeDate) {
          toast.error(
            "Examination Fee Generation Date is required for examination fees"
          );
          setIsSubmitting(false);
          return;
        }
        payload.applicableFrom = feeTypeEditData.examinationFeeDate;
      }

      await updateFeeType(editFee._id, payload);
      toast.success("Fee type updated successfully!");
      const el = document.getElementById("edit_fees_Type");
      if (el) {
        el.setAttribute("data-bs-dismiss", "modal");
        el.click();
        el.removeAttribute("data-bs-dismiss");
      }
      refreshList();
    } catch (error) {
      toast.error("Failed to update fee type");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!editingGroup) return;

    try {
      await deleteFeeGroup(editingGroup?._id as string);
      toast.success("Fee group deleted successfully");
      const el = document.getElementById("delete-modal");
      if (el) {
        el.setAttribute("data-bs-dismiss", "modal");
        el.click();
        el.removeAttribute("data-bs-dismiss");
      }
      setEditingGroup();
      refreshList();
    } catch (error) {
      toast.error("Failed to delete fee group");
    }
  };

  const handleDeleteFeeType = async () => {
    if (!editFee) return;
    try {
      await deleteFeeType(editFee._id);
      toast.success("Fee type deleted successfully");
      const el = document.getElementById("delete-modal");
      if (el) {
        el.setAttribute("data-bs-dismiss", "modal");
        el.click();
        el.removeAttribute("data-bs-dismiss");
      }
      refreshList();
      setEditFee(null);
    } catch (error) {
      toast.error("Failed to delete fee type");
    }
  };

  const fetchClassesForOptions = async (system: string) => {
    try {
      const response = await getAllClasses({
        system,
      });
      const options = response.classes.map((cls: Class) => ({
        value: cls._id,
        label: cls.name,
      }));
      setFilteredClassOptions(options);
    } catch (error) {
      console.error("Error fetching classes:", error);
    }
  };

  useEffect(() => {
    fetchClassesForOptions(selectedSystem);
  }, [selectedSystem]);

  const resetForm = () => {
    setFeeGroupData({
      name: "",
      description: "",
      status: false,
      session: currentSession?._id || "",
      system: selectedSystem,
    });
  };

  const resetFeeTypeForm = () => {
    setFeeTypeData({
      name: "",
      feeGroup: "",
      amount: 0,
      classes: [],
      status: true,
      system: selectedSystem,
      session: currentSession?._id || "",
      frequency: "",
      dueAfterDays: 0,
      examinationFeeDate: "",
    });
    setSelectedClasses([]);
  };

  return (
    <>
      {/* Add Fees Master */}
      <div className="modal fade" id="add_fees_master">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <div className="d-flex align-items-center">
                <h4 className="modal-title">Add Fees Master</h4>
                <span className="badge bg-soft-info ms-2">2024 - 2025</span>
              </div>
              <button
                type="button"
                className="btn-close custom-btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              >
                <i className="ti ti-x" />
              </button>
            </div>
            <form>
              <div className="modal-body" id="modal-datepicker2">
                <div className="row">
                  <div className="col-md-12">
                    <div className="mb-3">
                      <label className="form-label">Fees Group</label>
                      <CommonSelect
                        className="select"
                        options={feeGroup}
                        defaultValue={undefined}
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Fee Type</label>
                      <CommonSelect
                        className="select"
                        options={feesTypes}
                        defaultValue={undefined}
                      />
                    </div>
                  </div>
                  <div className="col-md-12">
                    <div className="row">
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="form-label">Due Date</label>
                          <div className="date-pic">
                            <DatePicker
                              className="form-control datetimepicker"
                              format={{
                                format: "DD-MM-YYYY",
                                type: "mask",
                              }}
                              getPopupContainer={getModalContainer2}
                              defaultValue=""
                              placeholder="Select Date"
                            />
                            <span className="cal-icon">
                              <i className="ti ti-calendar" />
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="form-label">Amount</label>
                          <input type="text" className="form-control" />
                        </div>
                      </div>
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Fine Type</label>
                      <div className="d-flex align-items-center check-radio-group">
                        <label className="custom-radio">
                          <input
                            type="radio"
                            name="radio"
                            value=""
                            checked={activeContent === ""}
                            onChange={handleContentChange}
                          />
                          <span className="checkmark" />
                          None
                        </label>
                        <label className="custom-radio percentage-radio">
                          <input
                            type="radio"
                            name="radio"
                            value="percentage"
                            onChange={handleContentChange}
                          />
                          <span className="checkmark" />
                          Percentage
                        </label>
                        <label className="custom-radio fixed-radio">
                          <input
                            type="radio"
                            name="radio"
                            value="fixed"
                            onChange={handleContentChange}
                          />
                          <span className="checkmark" />
                          Fixed
                        </label>
                      </div>
                    </div>
                    <div
                      className={`percentage-field ${
                        activeContent === "percentage"
                          ? "percentage-field-show"
                          : ""
                      } `}
                    >
                      <div className="row">
                        <div className="col-lg-6">
                          <div className="mb-3">
                            <label className="form-label">Percentage</label>
                            <input
                              type="text"
                              className="form-control"
                              placeholder="%"
                            />
                          </div>
                        </div>
                        <div className="col-lg-6">
                          <div className="mb-3">
                            <label className="form-label">Amount ($)</label>
                            <input
                              type="text"
                              className="form-control"
                              placeholder="$"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                    <div
                      className={`fixed-field ${
                        activeContent === "fixed" ? "fixed-field-show" : ""
                      } `}
                    >
                      <div className="row">
                        <div className="col-lg-12">
                          <div className="mb-3">
                            <label className="form-label">Amount ($)</label>
                            <input
                              type="text"
                              className="form-control"
                              placeholder="$"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="d-flex align-items-center justify-content-between">
                    <div className="status-title">
                      <h5>Status</h5>
                      <p>Change the Status by toggle </p>
                    </div>
                    <div className="form-check form-switch">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        role="switch"
                        id="switch-sm"
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <Link
                  to="#"
                  className="btn btn-light me-2"
                  data-bs-dismiss="modal"
                >
                  Cancel
                </Link>
                <Link
                  to="#"
                  data-bs-dismiss="modal"
                  className="btn btn-primary"
                >
                  Add Fees Master
                </Link>
              </div>
            </form>
          </div>
        </div>
      </div>
      {/* Add Fees Master*/}

      {/* Edit Fees Master */}
      <div className="modal fade" id="edit_fees_master">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <div className="d-flex align-items-center">
                <h4 className="modal-title">Edit Fees Master</h4>
                <span className="badge bg-soft-info ms-2">2024 - 2025</span>
              </div>
              <button
                type="button"
                className="btn-close custom-btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              >
                <i className="ti ti-x" />
              </button>
            </div>
            <form>
              <div className="modal-body" id="modal-datepicker">
                <div className="row">
                  <div className="col-md-12">
                    <div className="mb-3">
                      <label className="form-label">Fees Group</label>
                      <CommonSelect
                        className="select"
                        options={feeGroup}
                        defaultValue={feeGroup[1]}
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Fee Type</label>
                      <CommonSelect
                        className="select"
                        options={feesTypes}
                        defaultValue={feesTypes[1]}
                      />
                    </div>
                  </div>
                  <div className="col-md-12">
                    <div className="row">
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="form-label">Due Date</label>
                          <div className="date-pic">
                            <DatePicker
                              className="form-control datetimepicker"
                              format={{
                                format: "DD-MM-YYYY",
                                type: "mask",
                              }}
                              getPopupContainer={getModalContainer}
                              defaultValue={defaultValue}
                              placeholder="16 May 2024"
                            />
                            <span className="cal-icon">
                              <i className="ti ti-calendar" />
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="form-label">Amount</label>
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Enter Amount"
                            defaultValue="$500"
                          />
                        </div>
                      </div>
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Fine Type</label>
                      <div className="d-flex align-items-center check-radio-group">
                        <label className="custom-radio">
                          <input
                            type="radio"
                            name="radio"
                            value=""
                            checked={activeContent === ""}
                            onChange={handleContentChange}
                          />
                          <span className="checkmark" />
                          None
                        </label>
                        <label className="custom-radio percentage-radio">
                          <input
                            type="radio"
                            name="radio"
                            value="percentage"
                            onChange={handleContentChange}
                          />
                          <span className="checkmark" />
                          Percentage
                        </label>
                        <label className="custom-radio fixed-radio">
                          <input
                            type="radio"
                            name="radio"
                            value="fixed"
                            onChange={handleContentChange}
                          />
                          <span className="checkmark" />
                          Fixed
                        </label>
                      </div>
                    </div>
                    <div
                      className={`percentage-field ${
                        activeContent === "percentage"
                          ? "percentage-field-show"
                          : ""
                      } `}
                    >
                      <div className="row">
                        <div className="col-lg-6">
                          <div className="mb-3">
                            <label className="form-label">Percentage</label>
                            <input
                              type="text"
                              className="form-control"
                              placeholder="%"
                            />
                          </div>
                        </div>
                        <div className="col-lg-6">
                          <div className="mb-3">
                            <label className="form-label">Amount ($)</label>
                            <input
                              type="text"
                              className="form-control"
                              placeholder="$"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                    <div
                      className={`fixed-field ${
                        activeContent === "fixed" ? "fixed-field-show" : ""
                      } `}
                    >
                      <div className="row">
                        <div className="col-lg-12">
                          <div className="mb-3">
                            <label className="form-label">Amount ($)</label>
                            <input
                              type="text"
                              className="form-control"
                              placeholder="$"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="d-flex align-items-center justify-content-between">
                    <div className="status-title">
                      <h5>Status</h5>
                      <p>Change the Status by toggle </p>
                    </div>
                    <div className="form-check form-switch">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        role="switch"
                        id="switch-sm2"
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <Link
                  to="#"
                  className="btn btn-light me-2"
                  data-bs-dismiss="modal"
                >
                  Cancel
                </Link>
                <Link
                  to="#"
                  data-bs-dismiss="modal"
                  className="btn btn-primary"
                >
                  Save Changes
                </Link>
              </div>
            </form>
          </div>
        </div>
      </div>
      {/* Edit Fees Master*/}

      {/* Add Fee Type Modal */}
      <div className="modal fade" id="add_fees_Type">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h4 className="modal-title">Add Fee Type</h4>
              <button
                type="button"
                className="btn-close custom-btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
                onClick={resetFeeTypeForm}
              >
                <i className="ti ti-x" />
              </button>
            </div>
            <form onSubmit={handleCreateFeeTypeSubmit}>
              <div className="modal-body">
                <div className="row">
                  <div className="col-md-12">
                    <div className="mb-3">
                      <label className="form-label">Name</label>
                      <input
                        type="text"
                        className="form-control"
                        name="name"
                        value={feeTypeData.name}
                        onChange={handleFeeTypeInputChange}
                        required
                      />
                    </div>
                    <div className="mb-3">
                      <div className="d-flex justify-content-between">
                        <label className="form-label">Fees Group</label>
                      </div>
                      <CommonSelect
                        className="select"
                        options={feeGroups.map((group) => ({
                          value: group._id,
                          label: group.name,
                        }))}
                        value={
                          feeGroups.find(
                            (group) => group._id === feeTypeData.feeGroup
                          )
                            ? {
                                value: feeTypeData.feeGroup,
                                label:
                                  feeGroups.find(
                                    (group) =>
                                      group._id === feeTypeData.feeGroup
                                  )?.name || "",
                              }
                            : null
                        }
                        onChange={(selected) => {
                          setFeeTypeData((prev) => ({
                            ...prev,
                            feeGroup: selected?.value || "",
                          }));
                        }}
                        required
                      />
                    </div>
                  </div>

                  {/* Examination Fee Date Field - Only show when Examination fee group is selected */}
                  {isExaminationFeeGroup(feeTypeData.feeGroup) && (
                    <div className="mb-3">
                      <label className="form-label">
                        Examination Fee Generation Date
                      </label>
                      <div className="date-pic">
                        <DatePicker
                          className="form-control datetimepicker"
                          format={{
                            format: "DD-MM-YYYY",
                            type: "mask",
                          }}
                          value={
                            feeTypeData.examinationFeeDate
                              ? dayjs(feeTypeData.examinationFeeDate)
                              : null
                          }
                          onChange={(date) => {
                            setFeeTypeData((prev) => ({
                              ...prev,
                              examinationFeeDate: date
                                ? date.format("YYYY-MM-DD")
                                : "",
                            }));
                          }}
                          placeholder="Select Examination Fee Generation Date"
                        />
                        <span className="cal-icon">
                          <i className="ti ti-calendar" />
                        </span>
                      </div>
                    </div>
                  )}

                  <div className="mb-3">
                    <label className="form-label">Classes</label>
                    <MultiSelectSection
                      options={classes.map((item) => ({
                        label: item.name,
                        value: item._id,
                      }))}
                      value={selectedClasses}
                      onChange={(selected) => {
                        setSelectedClasses(selected);
                        setFeeTypeData((prev) => ({
                          ...prev,
                          classes: selected.map((cls) => cls.value),
                        }));
                      }}
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Session</label>
                    <CommonSelect
                      options={sessions}
                      value={sessions.find(
                        (item) => item._id === feeTypeData.session
                      )}
                      getOptionValue={(item) => item._id}
                      getOptionLabel={(item) => item.title}
                      onChange={(selected) => {
                        setFeeTypeData((prev) => ({
                          ...prev,
                          session: selected._id,
                        }));
                      }}
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Frequency</label>
                    <CommonSelect
                      options={frequencies}
                      value={frequencies.find(
                        (item) => item.value === feeTypeData.frequency
                      )}
                      onChange={(selected) => {
                        setFeeTypeData((prev) => ({
                          ...prev,
                          frequency: selected.value,
                        }));
                      }}
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Amount</label>
                    <input
                      type="number"
                      className="form-control"
                      name="amount"
                      value={feeTypeData.amount || ""}
                      onChange={(e) => {
                        const value = e.target.value;
                        setFeeTypeData((prev) => ({
                          ...prev,
                          amount: Number(value),
                        }));
                      }}
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Due After Days:</label>
                    <input
                      type="number"
                      className="form-control"
                      name="dueAfterDays"
                      value={feeTypeData.dueAfterDays}
                      onChange={(e) => {
                        const value = e.target.value;
                        setFeeTypeData((prev) => ({
                          ...prev,
                          dueAfterDays:
                            value === "" ? undefined : Number(value),
                        }));
                      }}
                      required
                    />
                  </div>
                  <div className="d-flex align-items-center justify-content-between">
                    <div className="status-title">
                      <h5>Status</h5>
                      <p>Change the Status by toggle </p>
                    </div>
                    <div className="form-check form-switch">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        role="switch"
                        checked={feeTypeData.status}
                        onChange={handleFeeTypeStatusChange}
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-light me-2"
                  data-bs-dismiss="modal"
                  onClick={resetFeeTypeForm}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Creating..." : "Add Fee Type"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Edit Fee Type Modal */}
      <div className="modal fade" id="edit_fees_Type">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h4 className="modal-title">Edit Fee Type</h4>
              <button
                type="button"
                className="btn-close custom-btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              >
                <i className="ti ti-x" />
              </button>
            </div>
            <form onSubmit={handleUpdateFeeTypeSubmit}>
              <div className="modal-body">
                <div className="row">
                  <div className="col-md-12">
                    <div className="mb-3">
                      <label className="form-label">Name</label>
                      <input
                        type="text"
                        className="form-control"
                        name="name"
                        value={feeTypeEditData.name}
                        onChange={handleFeeTypeEditInputChange}
                        required
                      />
                    </div>
                    <div className="mb-3">
                      <div className="d-flex justify-content-between">
                        <label className="form-label">Fees Group</label>
                      </div>
                      <CommonSelect
                        className="select"
                        options={feeGroups}
                        getOptionValue={(item) => item._id}
                        getOptionLabel={(item) => item.name}
                        value={feeGroups.find(
                          (group) => group._id === feeTypeEditData.feeGroup
                        )}
                        onChange={(selected) => {
                          setFeeTypeEditData((prev) => ({
                            ...prev,
                            feeGroup: selected?._id || "",
                          }));
                        }}
                        required
                      />
                    </div>

                    {/* Examination Fee Date Field - Only show when Examination fee group is selected */}
                    {isExaminationFeeGroup(feeTypeEditData.feeGroup) && (
                      <div className="mb-3">
                        <label className="form-label">
                          Examination Fee Generation Date
                        </label>
                        <div className="date-pic">
                          <DatePicker
                            className="form-control datetimepicker"
                            format={{
                              format: "DD-MM-YYYY",
                              type: "mask",
                            }}
                            value={
                              feeTypeEditData.examinationFeeDate
                                ? dayjs(feeTypeEditData.examinationFeeDate)
                                : null
                            }
                            onChange={(date) => {
                              setFeeTypeEditData((prev) => ({
                                ...prev,
                                examinationFeeDate: date
                                  ? date.format("YYYY-MM-DD")
                                  : "",
                              }));
                            }}
                            placeholder="Select Examination Fee Generation Date"
                          />
                          <span className="cal-icon">
                            <i className="ti ti-calendar" />
                          </span>
                        </div>
                      </div>
                    )}

                    <div className="mb-3">
                      <label className="form-label">Classes</label>
                      <MultiSelectSection
                        options={filteredClassOptions}
                        value={selectedEditClasses}
                        onChange={(selected) => {
                          setSelectedEditClasses(selected);
                          setFeeTypeEditData((prev) => ({
                            ...prev,
                            classes: selected.map((cls) => cls.value),
                          }));
                        }}
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Session</label>
                      <CommonSelect
                        isDisabled
                        options={sessions}
                        value={sessions.find(
                          (item) => item._id === feeTypeEditData.session
                        )}
                        getOptionValue={(item) => item._id}
                        getOptionLabel={(item) => item.title}
                        onChange={(selected) => {
                          setFeeTypeEditData((prev) => ({
                            ...prev,
                            session: selected._id,
                          }));
                        }}
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Frequency</label>
                      <CommonSelect
                        options={frequencies}
                        value={frequencies.find(
                          (item) => item.value === feeTypeEditData.frequency
                        )}
                        onChange={(selected) => {
                          setFeeTypeEditData((prev) => ({
                            ...prev,
                            frequency: selected.value,
                          }));
                        }}
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Amount</label>
                      <input
                        type="number"
                        className="form-control"
                        name="amount"
                        value={feeTypeEditData.amount || ""}
                        onChange={(e) => {
                          const value = e.target.value;
                          setFeeTypeEditData((prev) => ({
                            ...prev,
                            amount: value === "" ? undefined : Number(value),
                          }));
                        }}
                        required
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Due After Days:</label>
                      <input
                        type="number"
                        className="form-control"
                        name="dueAfterDays"
                        value={feeTypeEditData.dueAfterDays}
                        onChange={(e) => {
                          const value = e.target.value;
                          setFeeTypeEditData((prev) => ({
                            ...prev,
                            dueAfterDays:
                              value === "" ? undefined : Number(value),
                          }));
                        }}
                        required
                      />
                    </div>
                    <div className="d-flex align-items-center justify-content-between">
                      <div className="status-title">
                        <h5>Status</h5>
                        <p>Change the Status by toggle </p>
                      </div>
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          role="switch"
                          checked={feeTypeEditData.status}
                          onChange={handleFeeTypeStatusChange}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-light me-2"
                  data-bs-dismiss="modal"
                  onClick={() => {
                    setEditFee(null);
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Updating..." : "Save Changes"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Add New Fees Group Modal */}
      <div className="modal fade" id="add_new_fees_group">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h4 className="modal-title">Add New Fees Group</h4>
              <button
                type="button"
                className="btn-close custom-btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              >
                <i className="ti ti-x" />
              </button>
            </div>
            <form>
              <div className="modal-body">
                <div className="row">
                  <div className="col-md-12">
                    <div className="mb-3">
                      <label className="form-label">Name</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Enter Name"
                        defaultValue=""
                      />
                    </div>
                  </div>
                  <div className="d-flex align-items-center justify-content-between">
                    <div className="status-title">
                      <h5>Status</h5>
                      <p>Change the Status by toggle </p>
                    </div>
                    <div className="form-check form-switch">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        role="switch"
                        id="switch-sm3"
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <Link
                  to="#"
                  className="btn btn-light me-2"
                  data-bs-dismiss="modal"
                >
                  Cancel
                </Link>
                <Link
                  to="#"
                  data-bs-dismiss="modal"
                  className="btn btn-primary"
                >
                  Add Fee Type
                </Link>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Add Fees Group Modal */}
      <div className="modal fade" id="add_fees_group">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h4 className="modal-title">Add Fees Group</h4>
              <button
                type="button"
                className="btn-close custom-btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
                onClick={resetForm}
              >
                <i className="ti ti-x" />
              </button>
            </div>
            <form onSubmit={handleCreateSubmit}>
              <div className="modal-body">
                <div className="row">
                  <div className="col-md-12">
                    <div className="mb-3">
                      <label className="form-label">Fees Group Name</label>
                      <input
                        type="text"
                        className="form-control"
                        name="name"
                        value={feeGroupData.name}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Description</label>
                      <textarea
                        className="form-control"
                        rows={4}
                        name="description"
                        value={feeGroupData.description}
                        onChange={handleInputChange}
                      />
                    </div>
                    <div className="d-flex align-items-center justify-content-between">
                      <div className="status-title">
                        <h5>Status</h5>
                        <p>Change the Status by toggle</p>
                      </div>
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          role="switch"
                          checked={feeGroupData.status}
                          onChange={handleStatusChange}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-light me-2"
                  data-bs-dismiss="modal"
                  onClick={resetForm}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Creating..." : "Add Fees Group"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Edit Fees Group Modal */}
      <div className="modal fade" id="edit_fees_group">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h4 className="modal-title">Edit Fees Group</h4>
              <button
                type="button"
                className="btn-close custom-btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
                onClick={() => setEditingGroup()}
              >
                <i className="ti ti-x" />
              </button>
            </div>
            <form onSubmit={handleEditSubmit}>
              <div className="modal-body">
                <div className="row">
                  <div className="col-md-12">
                    <div className="mb-3">
                      <label className="form-label">Fees Group Name</label>
                      <input
                        type="text"
                        className="form-control"
                        name="name"
                        value={feeGroupData.name}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Description</label>
                      <textarea
                        className="form-control"
                        rows={4}
                        name="description"
                        value={feeGroupData.description}
                        onChange={handleInputChange}
                      />
                    </div>
                    <div className="d-flex align-items-center justify-content-between">
                      <div className="status-title">
                        <h5>Status</h5>
                        <p>Change the Status by toggle</p>
                      </div>
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          role="switch"
                          checked={feeGroupData.status}
                          onChange={handleStatusChange}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-light me-2"
                  data-bs-dismiss="modal"
                  onClick={() => setEditingGroup()}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Updating..." : "Save Changes"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <div className="modal fade" id="delete-modal">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-body text-center">
              <span className="delete-icon">
                <i className="ti ti-trash-x" />
              </span>
              <h4>Confirm Deletion</h4>
              <p>
                Are you sure you want to delete this{" "}
                {editFee ? "fee type" : "fee group"}?
              </p>
              <div className="d-flex justify-content-center">
                <button
                  className="btn btn-light me-3"
                  data-bs-dismiss="modal"
                  onClick={() => {
                    setEditingGroup();
                    setEditFee(null);
                  }}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-danger"
                  onClick={editFee ? handleDeleteFeeType : handleDelete}
                >
                  Yes, Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default FeesModal;
